import React, { useEffect, useState } from 'react';
import { onAuthStateChanged, setPersistence, browserSessionPersistence } from 'firebase/auth';
import { ConfigProvider } from 'antd';
import { auth } from './firebase';
import Login from './components/Login';
import FileUpload from './components/FileUpload';
import AdminPanel from './components/AdminPanel';
import ChangePassword from './components/ChangePassword';
import ResetPassword from './components/ResetPassword';

const FUNCTIONS_BASE_URL = process.env.REACT_APP_FUNCTIONS_BASE_URL;

const theme = {
  token: {
    colorPrimary: '#F96700',
    colorText: '#212121',
    fontFamily: "'DM Sans', Arial, sans-serif",
    borderRadius: 8,
  },
};

function App() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [forcePasswordChange, setForcePasswordChange] = useState(false);
  const [loading, setLoading] = useState(true);

  // Detect password reset links: ?mode=resetPassword&oobCode=...
  const [oobCode, setOobCode] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('mode') === 'resetPassword' ? p.get('oobCode') : null;
  });

  useEffect(() => {
    setPersistence(auth, browserSessionPersistence).catch(() => {});
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser && FUNCTIONS_BASE_URL) {
        try {
          // Force-refresh the token to ensure custom claims (e.g. forcePasswordChange) are current
          const tokenResult = await currentUser.getIdTokenResult(true);
          const adminRes = await fetch(`${FUNCTIONS_BASE_URL}/checkAdmin`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${tokenResult.token}` },
          });
          if (adminRes.ok) {
            const data = await adminRes.json();
            setIsAdmin(data.isAdmin === true);
          } else {
            setIsAdmin(false);
          }
          setForcePasswordChange(tokenResult.claims.forcePasswordChange === true);
        } catch {
          setIsAdmin(false);
          setForcePasswordChange(false);
        }
      } else {
        setIsAdmin(false);
        setForcePasswordChange(false);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // Handle password reset links — shown regardless of auth state
  if (oobCode) {
    return (
      <ConfigProvider theme={theme}>
        <ResetPassword
          oobCode={oobCode}
          onDone={() => {
            window.history.replaceState({}, '', window.location.pathname);
            setOobCode(null);
          }}
        />
      </ConfigProvider>
    );
  }

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'var(--surface)',
      }}>
        <img src="/assets/default-loading.gif" alt="Loading…" style={{ width: 48, height: 48 }} />
      </div>
    );
  }

  return (
    <ConfigProvider theme={theme}>
      {user ? (
        forcePasswordChange ? (
          <ChangePassword
            user={user}
            forced
            onPasswordChanged={() => setForcePasswordChange(false)}
          />
        ) : isAdmin ? (
          <AdminPanel user={user} />
        ) : (
          <FileUpload user={user} />
        )
      ) : (
        <Login />
      )}
    </ConfigProvider>
  );
}

export default App;

